import React from 'react';
import { Loader2, Clock } from 'lucide-react';

interface AnalysisProgressProps {
  progress: number;
  currentStep: string | null;
  estimatedTimeRemaining: number | null;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  progress,
  currentStep,
  estimatedTimeRemaining,
}) => {
  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-700/50">
        <h2 className="text-xl font-semibold text-white">Analysis in Progress</h2>
        <p className="text-sm text-slate-400 mt-1">
          Processing your files and generating comprehensive insights
        </p>
      </div>

      <div className="p-8">
        {/* Circular Progress Indicator */}
        <div className="flex flex-col items-center justify-center space-y-6">
          {/* Progress Circle */}
          <div className="relative">
            <svg className="w-32 h-32 transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-slate-700"
              />
              {/* Progress circle */}
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
                className="text-indigo-500 transition-all duration-500"
                strokeLinecap="round"
              />
            </svg>
            {/* Center icon and percentage */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-indigo-400 mx-auto mb-1 animate-spin" />
                <p className="text-2xl font-bold text-white">{progress}%</p>
              </div>
            </div>
          </div>

          {/* Current Step */}
          {currentStep && (
            <div className="text-center">
              <p className="text-lg font-medium text-white">{currentStep}</p>
            </div>
          )}

          {/* Estimated Time Remaining */}
          {estimatedTimeRemaining !== null && estimatedTimeRemaining > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700">
              <Clock className="w-4 h-4 text-slate-400" />
              <p className="text-sm text-slate-300">
                Estimated time remaining: {formatTime(estimatedTimeRemaining)}
              </p>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="mt-8 space-y-3">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${progress >= 10 ? 'bg-indigo-500' : 'bg-slate-700'}`} />
            <p className={`text-sm ${progress >= 10 ? 'text-white' : 'text-slate-500'}`}>
              Uploading files
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${progress >= 30 ? 'bg-indigo-500' : 'bg-slate-700'}`} />
            <p className={`text-sm ${progress >= 30 ? 'text-white' : 'text-slate-500'}`}>
              Extracting commission data
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${progress >= 50 ? 'bg-indigo-500' : 'bg-slate-700'}`} />
            <p className={`text-sm ${progress >= 50 ? 'text-white' : 'text-slate-500'}`}>
              Analyzing growth patterns
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${progress >= 70 ? 'bg-indigo-500' : 'bg-slate-700'}`} />
            <p className={`text-sm ${progress >= 70 ? 'text-white' : 'text-slate-500'}`}>
              Calculating valuations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${progress >= 90 ? 'bg-indigo-500' : 'bg-slate-700'}`} />
            <p className={`text-sm ${progress >= 90 ? 'text-white' : 'text-slate-500'}`}>
              Generating report
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
          <p className="text-sm text-indigo-300">
            Your analysis is being processed. You can safely navigate away and come back later.
            We'll notify you when it's complete.
          </p>
        </div>
      </div>
    </div>
  );
};
